// POPIA / GDPR data rights (121): self-service "download my data" + "delete my
// account" on the profile page. Copy here is LEGAL-REVIEW-PENDING (ship gate).

import { useEffect, useState } from "react";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import type { AccountDeletionStatus } from "@dugads/shared";
import { Download, Loader2, ShieldAlert } from "lucide-react";

export function PrivacyDataCard() {
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [deletion, setDeletion] = useState<AccountDeletionStatus | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getAccountDeletionStatus()
      .then((r) => setDeletion(r.status))
      .catch(() => {});
  }, []);

  async function handleExport() {
    setExporting(true);
    setExportMsg(null);
    setError(null);
    try {
      const res = await api.requestDataExport();
      if (res.request.download_available) {
        await api.downloadDataExport(res.request.id);
        setExportMsg("Your data download has started.");
      } else {
        setExportMsg("We're preparing your data. Try again shortly.");
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Could not prepare your data export.");
    } finally {
      setExporting(false);
    }
  }

  async function handleRequestDeletion() {
    setBusy(true);
    setError(null);
    try {
      await api.requestAccountDeletion();
      setDeleteRequested(true);
      setConfirmingDelete(false);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Could not start account deletion.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelDeletion() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.cancelAccountDeletion();
      setDeletion(res.status);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Could not cancel deletion.");
    } finally {
      setBusy(false);
    }
  }

  const purgeDate = deletion?.scheduled_purge_at
    ? new Date(deletion.scheduled_purge_at).toLocaleDateString()
    : null;

  return (
    <Card className="mt-6" data-region="privacy-data">
      <CardHeader>
        <CardTitle className="text-base">Privacy &amp; your data</CardTitle>
        <CardDescription>
          Download a copy of the personal data we hold about you, or permanently
          delete your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5 text-sm">
        {/* Export */}
        <div className="flex flex-col gap-2">
          <div className="font-medium">Download my data</div>
          <p className="text-muted-foreground">
            We'll assemble your personal data into a single file you can save.
          </p>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            className="w-fit"
            data-action="export-data"
          >
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {exporting ? "Preparing..." : "Download my data"}
          </Button>
          {exportMsg && <p className="text-muted-foreground">{exportMsg}</p>}
        </div>

        <div className="border-t border-border" />

        {/* Deletion */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 font-medium text-destructive">
            <ShieldAlert className="h-4 w-4" /> Delete my account
          </div>

          {deletion?.deletion_pending_at ? (
            <div className="flex flex-col gap-2" data-state="deletion-pending">
              <p className="text-muted-foreground">
                Your account is scheduled for permanent deletion
                {purgeDate ? ` on ${purgeDate}` : ""}. You can cancel any time
                before then.
              </p>
              <Button
                variant="outline"
                onClick={handleCancelDeletion}
                disabled={busy}
                className="w-fit"
                data-action="cancel-deletion"
              >
                Cancel deletion
              </Button>
            </div>
          ) : deleteRequested ? (
            <p className="text-muted-foreground" data-state="deletion-email-sent">
              Check your email for a link to confirm deletion. Your account is
              not deleted until you confirm.
            </p>
          ) : confirmingDelete ? (
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-muted-foreground">
                {/* LEGAL-REVIEW-PENDING copy */}
                This permanently deletes your account. Some records we are
                legally required to keep (such as order and match history) will
                be anonymised rather than deleted. We'll email you a link to
                confirm; after you confirm, deletion completes in 30 days.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={handleRequestDeletion}
                  disabled={busy}
                  data-action="confirm-request-deletion"
                >
                  {busy ? "Sending..." : "Email me a confirmation link"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground">
                Permanently delete your account. This cannot be undone after the
                30-day grace period.
              </p>
              <Button
                variant="outline"
                onClick={() => setConfirmingDelete(true)}
                className="w-fit text-destructive"
                data-action="start-deletion"
              >
                Delete my account
              </Button>
            </>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
